import { Component, Input, ViewChildren, QueryList, Output, EventEmitter, ViewChild, OnInit, inject, ChangeDetectionStrategy } from '@angular/core';
import { DatetimePickerComponent } from '../datetime-picker/datetime-picker.component';
import { Trigger } from './models';
import { Timespan } from '../utils/timespan';
import { WeekdaySelectorComponent } from './weekday-selector.component';

import { SvgIconComponent } from '../icons/svg-icon.component';
import { DxDropDownBoxModule } from 'devextreme-angular';
import { TimeZoneService } from '../globalization/services/time-zone-service';
import { FormsModule } from '@angular/forms';

@Component({
    templateUrl: './trigger-item.component.html',
    selector: 'app-trigger-item',
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [WeekdaySelectorComponent, DatetimePickerComponent, SvgIconComponent, DxDropDownBoxModule, FormsModule]
})
export class TriggerItemComponent implements OnInit {
    @Input() readonly = false;
    @Input() hideTriggerStart = false;
    @Input() hideTriggerEnd = false;
    @Input() requireWeekdaySelection = false;
    @Input() validateTriggerTimes = false;
    @Input() trigger: Trigger = new Trigger();
    @Output() triggerChange = new EventEmitter<Trigger>();
    @Output() triggerRemove = new EventEmitter<Trigger>();

    @ViewChild(WeekdaySelectorComponent) weekdayPicker: WeekdaySelectorComponent;
    @ViewChildren(DatetimePickerComponent) timePickers: QueryList<DatetimePickerComponent>;

    
    timeZoneService= inject(TimeZoneService);
    

    ngOnInit(): void{
        this.trigger.TimeZoneIdentifier = this.trigger.TimeZoneIdentifier ?? this.timeZoneService.localTimeZone()?.Identifier;
    }

    removeTrigger() {
        this.triggerRemove.emit(this.trigger);
    }

    changeTrigger() {
        this.triggerChange.emit(this.trigger);
    }

    validateSelectedStartTime = (startTime: Date) => {
        // If one or both date pickers are hidden then there is no reason to validate.
        // If validation is specifcally requested to be off then nothing will be validated.
        // An initial validation occurs before @ViewChildren is resolved, so skip this first check.
        if (this.hideTriggerStart || this.hideTriggerEnd || !this.validateTriggerTimes || !this.timePickers) {
            return true;
        }

        const endTime = this.trigger.EndTime ? Timespan.convertTimeStringToDate(this.trigger.EndTime) : null;

        if (startTime === undefined || startTime === null || endTime === null || startTime > endTime) {
            // This ensures that the StartTime control is also invalid.
            this.timePickers.forEach(x => x.datepicker.instance.option('isValid', false));
            return false;
        } else {
            // This ensures that the StartTime control is also valid.
            this.timePickers.forEach(x => x.datepicker.instance.option('isValid', true));
            return true;
        }
    };

    validateSelectedEndTime = (endTime: Date) => {
        // If one or both date pickers are hidden then there is no reason to validate.
        // If validation is specifcally requested to be off then nothing will be validated.
        // An initial validation occurs before @ViewChildren is resolved, so skip this first check.
        if (this.hideTriggerStart || this.hideTriggerEnd || !this.validateTriggerTimes || !this.timePickers) {
            return true;
        }

        const startTime = this.trigger.StartTime ? Timespan.convertTimeStringToDate(this.trigger.StartTime) : null;

        if (startTime === null || endTime === undefined || endTime === null || startTime > endTime) {
            // This ensures that the StartTime control is also invalid.
            this.timePickers.forEach(x => x.datepicker.instance.option('isValid', false));
            return false;
        } else {
            // This ensures that the StartTime control is also valid.
            this.timePickers.forEach(x => x.datepicker.instance.option('isValid', true));
            return true;
        }
    };

    get isTriggerValid(): boolean {
        let isValid = true;
        // If one or both date pickers are hidden then there is no reason to validate.
        // If validation is specifcally requested to be off then nothing will be validated.
        if (this.hideTriggerStart || this.hideTriggerEnd || !this.validateTriggerTimes) {
            return isValid;
        }

        this.timePickers.forEach(x => {
            if (isValid) {
                isValid = x.datepicker.instance.option('isValid');
            }
        });

        if (isValid) {
            isValid = this.weekdayPicker.weekdayPicker.instance.option('isValid');
        }

        return isValid;
    }
}
